// src/pages/PurchaseHistory.jsx
import { useAuth0 } from "@auth0/auth0-react";
import ListSubscriptions from "components/product/ListSubscriptions";
import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  selectSubscriptions,
  fetchSubscriptionsForCustomer,
} from "store/slices/subscriptionsSlice";

import {
  Breadcrumb,
  Loading,
  ErrorMessage,
  Paragraph,
  TextButton,
  PreferDeliveryDaySelector,
} from "common";
import { OrdersTrackingList } from "components";

import { ListProductNoFilter } from "@/components/product";
import { Select, SearchField } from "@/design-system";
import {
  fetchPurchaseHistory,
  clearPurchaseHistory,
  selectTransactions,
  selectPurchaseHistoryStatus,
  selectPurchaseHistoryError,
  selectShouldFetch,
  selectLoadedMonth,
  selectFirstOrderDate,
} from "@/redux/slices/purchaseHistorySlice";

// The orders list has no "Show N" control anymore (the reference toolbar is
// month + search only), so fetch a fixed window wide enough for the month filter.
const ORDERS_LIMIT = "100";

/* small date helpers for summary */
function daysInMonth(y, m) {
  return new Date(y, m + 1, 0).getDate();
}
function addMonthsSafe(d0, n) {
  const d = new Date(d0.getTime());
  const day = d.getDate();
  const tm = d.getMonth() + n;
  const ty = d.getFullYear() + Math.floor(tm / 12);
  const nm = ((tm % 12) + 12) % 12;
  const end = daysInMonth(ty, nm);
  const clamped = Math.min(day, end);
  const res = new Date(d);
  res.setFullYear(ty, nm, clamped);
  return res;
}
function nextFromToday(s) {
  const n = parseInt(s || "1", 10);
  return addMonthsSafe(new Date(), Number.isFinite(n) ? n : 1);
}
function formatLocalDateToronto(d) {
  return d.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "America/Toronto",
  });
}
// Month key ("YYYY-MM", the backend `month` param) and its display label.
function monthKeyOf(date) {
  const d = new Date(date);
  if (isNaN(d)) return "";
  // en-CA renders YYYY-MM-DD; keep the year-month prefix.
  return d
    .toLocaleDateString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "America/Toronto",
    })
    .slice(0, 7);
}
function monthLabelOf(key) {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return new Date(y, m - 1, 1).toLocaleDateString("en-CA", {
    month: "short",
    year: "numeric",
  });
}

export default function PurchaseHistory() {
  const { getAccessTokenSilently } = useAuth0();
  const userInfo = useSelector((state) => state.user.info);
  const dispatch = useDispatch();
  const location = useLocation();

  const subscriptions = useSelector(selectSubscriptions);

  const transactions = useSelector(selectTransactions);
  const loading = useSelector(selectPurchaseHistoryStatus) === "loading";
  const error = useSelector(selectPurchaseHistoryError);
  const shouldFetch = useSelector(selectShouldFetch);
  const loadedMonth = useSelector(selectLoadedMonth);
  const firstOrderDate = useSelector(selectFirstOrderDate);

  // The tab button row was removed (a different entry point for buyAgain /
  // subscriptions is coming); the branches below stay, pinned to "orders".
  const [activeTab] = useState("orders");

  // Orders-tab toolbar state — client-side filters over the loaded window
  const [ordersMonth, setOrdersMonth] = useState("all");
  const [ordersSearch, setOrdersSearch] = useState("");

  // Newest first. `trandate` is a date with no time, so same-day orders tie;
  // the NetSuite internal id is monotonic and breaks the tie (SO119361 above
  // SO119354). Mirrors the backend's ORDER BY t.trandate DESC, t.id DESC.
  const salesOrders = useMemo(() => {
    return transactions
      .filter((o) => o.type === "SalesOrd" || !o.type)
      .slice()
      .sort(
        (a, b) =>
          new Date(b.trandate) - new Date(a.trandate) ||
          Number(b.id) - Number(a.id)
      );
  }, [transactions]);

  // Every month from now back to the customer's first order, so months older
  // than the loaded window are still selectable (picking one fetches it
  // server-side). Falls back to the loaded orders' months until the backend
  // reports firstOrderDate.
  const monthOptions = useMemo(() => {
    const firstKey = monthKeyOf(firstOrderDate);
    let keys;
    if (firstKey) {
      keys = [];
      for (
        let d = new Date(), i = 0;
        i < 600; // hard stop against a corrupt far-past date
        d = addMonthsSafe(d, -1), i++
      ) {
        const key = monthKeyOf(d);
        keys.push(key);
        if (key <= firstKey) break;
      }
    } else {
      keys = [...new Set(salesOrders.map((o) => monthKeyOf(o.trandate)))];
    }
    return [
      { value: "all", label: "All months" },
      ...keys.filter(Boolean).map((key) => ({
        value: key,
        label: monthLabelOf(key),
      })),
    ];
  }, [firstOrderDate, salesOrders]);

  const visibleOrders = useMemo(() => {
    const q = ordersSearch.trim().toLowerCase();
    return salesOrders.filter((o) => {
      if (ordersMonth !== "all" && monthKeyOf(o.trandate) !== ordersMonth)
        return false;
      if (!q) return true;
      const haystack = [
        o.tranid,
        o.transactionnumber,
        o.trandisplayname,
        ...(o.returnNumbers || []),
        ...(o.lines || []).map((l) => l.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [salesOrders, ordersMonth, ordersSearch]);

  const ordersEmptyMessage =
    ordersMonth !== "all"
      ? `No orders in ${monthLabelOf(ordersMonth)} — pick another month above.`
      : ordersSearch.trim()
        ? `No orders match "${ordersSearch.trim()}".`
        : "You don't have any orders yet.";

  // "All months" only holds the newest ORDERS_LIMIT rows; when the window is
  // full, older orders exist and are reachable via the month filter.
  const isWindowTruncated =
    ordersMonth === "all" && transactions.length >= Number(ORDERS_LIMIT);

  // Fetch data effect — refires when the selected month differs from what's
  // loaded (month changes are server-side fetches, not client-side filters).
  useEffect(() => {
    const fetchData = async () => {
      if ((shouldFetch || loadedMonth !== ordersMonth) && userInfo?.id) {
        try {
          await dispatch(
            fetchPurchaseHistory({
              userId: userInfo.id,
              limit: ORDERS_LIMIT,
              month: ordersMonth,
              getAccessTokenSilently,
            })
          );
        } catch (err) {
          // Error handling is managed by Redux
        }
      }
    };

    fetchData();
  }, [
    shouldFetch,
    loadedMonth,
    ordersMonth,
    userInfo?.id,
    getAccessTokenSilently,
    dispatch,
  ]);

  // Force refresh when coming from checkout to ensure new order appears
  useEffect(() => {
    const isFromCheckout =
      location.state?.paymentIntent ||
      location.state?.paymentSuccessMessage ||
      location.state?.fromCheckout;

    if (isFromCheckout && userInfo?.id) {
      // Clear cache first to ensure fresh data
      dispatch(clearPurchaseHistory());

      // Then fetch fresh data
      const forceRefresh = async () => {
        try {
          await dispatch(
            fetchPurchaseHistory({
              userId: userInfo.id,
              limit: ORDERS_LIMIT,
              getAccessTokenSilently,
              forceRefresh: true,
            })
          );
        } catch (err) {
          // Error handling is managed by Redux
        }
      };

      forceRefresh();

      // Clear the state to prevent re-fetching on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state, userInfo?.id, getAccessTokenSilently, dispatch]);

  // fetch subscriptions when entering the tab
  useEffect(() => {
    if (activeTab === "subscriptions" && userInfo?.id) {
      dispatch(
        fetchSubscriptionsForCustomer({
          customerId: userInfo.id,
          getAccessTokenSilently,
        })
      );
    }
  }, [activeTab, userInfo?.id, getAccessTokenSilently, dispatch]);

  // summary
  const nextDates = subscriptions.map((s) => nextFromToday(s.interval));
  const earliestNextDate =
    nextDates.length > 0
      ? nextDates.reduce((min, d) => (d < min ? d : min), nextDates[0])
      : null;

  return (
    <div className="mx-auto max-w-container px-[var(--gutter)] pt-[var(--space-5)] pb-[var(--space-20)]">
      <Breadcrumb path={["Home", "Orders & tracking"]} />

      {activeTab === "orders" ? (
        <>
          <div className="flex items-end justify-between gap-[var(--space-4)] flex-wrap mt-[var(--space-4)] mb-[22px]">
            <h2 className="font-medium text-[26px] leading-[1.2] tracking-[-0.01em] m-0 whitespace-nowrap">
              Orders &amp; tracking
            </h2>
            <div className="acct-tools flex items-center gap-[10px] flex-wrap">
              <div className="select-pill w-[168px]">
                <Select
                  value={ordersMonth}
                  onChange={setOrdersMonth}
                  options={monthOptions}
                />
              </div>
              <div className="w-[260px] max-w-full">
                <SearchField
                  value={ordersSearch}
                  onChange={setOrdersSearch}
                  placeholder="Search orders"
                  buttonLabel=""
                />
              </div>
            </div>
          </div>

          {!loading && isWindowTruncated && (
            <p className="text-xs text-ink-3 mt-[-12px] mb-[18px]">
              Showing your {ORDERS_LIMIT} most recent orders — pick a month
              above to see older ones.
            </p>
          )}

          {loading ? (
            <Loading text="Loading purchase history..." className="py-6" />
          ) : error ? (
            <ErrorMessage message={error} />
          ) : (
            <OrdersTrackingList
              orders={visibleOrders}
              emptyMessage={ordersEmptyMessage}
            />
          )}
        </>
      ) : activeTab === "buyAgain" ? (
        <div>
          {userInfo?.id ? (
            <ListProductNoFilter searchIds={userInfo.id} by="orderHistory" />
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Profile Setup Required
              </h3>
              <Paragraph className="text-gray-500 mb-4">
                Please complete your profile setup to view your purchase
                history.
              </Paragraph>
              <TextButton
                className="text-blue-600 hover:text-blue-800"
                onClick={() => (window.location.href = "/profile")}
              >
                Complete Profile Setup
              </TextButton>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ListSubscriptions />
          </div>

          <div className="space-y-4">
            <div className="py-2 border-t border-gray-200 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700">Earliest next order</span>
                <span className="text-gray-900">
                  {earliestNextDate
                    ? formatLocalDateToronto(earliestNextDate)
                    : "—"}
                </span>
              </div>
            </div>

            <PreferDeliveryDaySelector className="py-2 border-t border-gray-200" />
          </div>
        </div>
      )}
    </div>
  );
}
